/**
 * Dunning schedule — the single source of truth for the PAST_DUE grace policy.
 *
 * Lives in `@property/shared` because two very different consumers must agree on it:
 * the API's workflow steps (`apps/api/src/workflows/subscription-past-due/`) and the
 * CDK `Wait` durations that actually enforce it
 * (`infra/lib/constructs/workflows.ts`). When these drifted apart, the emails promised
 * a deadline the state machine didn't keep.
 *
 * Grace is measured from the immutable `subscriptions.pastDueSince`, never from
 * `lastWebhookAt` — a gateway retrying its dunning webhooks would otherwise push the
 * deadline back indefinitely.
 */

/** Days of grace from the first failed payment before service is suspended. */
export const DUNNING_GRACE_DAYS = 7;

/** Days after `pastDueSince` on which a reminder email goes out. */
export const DUNNING_REMINDER_DAYS = [3, 5] as const;

export interface DunningStage {
  /** Days after `pastDueSince` this stage runs. */
  day: number;
  /** Days to wait since the previous stage — what a Step Functions `Wait` needs. */
  waitDays: number;
  action: 'remind' | 'suspend';
  /** Stable, unique suffix for CDK construct ids. Changing one replaces a state. */
  id: string;
}

/**
 * The ladder, derived so the day numbers above stay the only thing to edit. Each stage
 * carries the gap since the previous one, because Step Functions `Wait` states are
 * relative. Adding a day-6 reminder is one entry in `DUNNING_REMINDER_DAYS`.
 */
export const DUNNING_STAGES: readonly DunningStage[] = buildDunningStages();

function buildDunningStages(): DunningStage[] {
  const reminders = [...DUNNING_REMINDER_DAYS].sort((a, b) => a - b);

  const invalid = reminders.find((d) => d <= 0 || d >= DUNNING_GRACE_DAYS);
  if (invalid !== undefined) {
    throw new Error(
      `Dunning reminder day ${invalid} must fall strictly inside the ${DUNNING_GRACE_DAYS}-day grace window`,
    );
  }

  const days = [...reminders, DUNNING_GRACE_DAYS];
  let previousDay = 0;
  return days.map((day, i) => {
    const stage: DunningStage = {
      day,
      waitDays: day - previousDay,
      action: i === days.length - 1 ? 'suspend' : 'remind',
      id: i === days.length - 1 ? 'Suspend' : `Reminder${i + 1}`,
    };
    previousDay = day;
    return stage;
  });
}
